import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Check, Loader2, X, Sparkles, Crown } from 'lucide-react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { axiosClient } from '@/api/axiosClient';
import { queryKeys } from '@/api/queryKeys';
import { getPlanFeatures, isYearlyPlan } from '@/config/plans';
import toast from 'react-hot-toast';
import { trackEvent } from '@/lib/analytics';

interface PricingModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
}

interface HubPlan {
  id: number | string;
  name: string;
  slug: string;
  description: string;
  price: number | string;
  billing_period: 'MONTHLY' | 'YEARLY';
  features: Record<string, boolean | number>;
  limits: Record<string, number>;
  is_popular?: boolean;
}

interface PlansResponse {
  plans: HubPlan[];
}

interface SubscriptionInfo {
  plan_id: string | null;
  plan_name: string;
  plan_slug: string;
  status: string;
  is_active: boolean;
}

function calculateMaxYearlyDiscount(plans: HubPlan[]): number | null {
  const monthlyPlans = plans.filter(p =>
    p.billing_period === 'MONTHLY' && !p.slug.includes('free')
  );
  const yearlyPlans = plans.filter(p =>
    p.billing_period === 'YEARLY' && !p.slug.includes('free')
  );

  if (monthlyPlans.length === 0 || yearlyPlans.length === 0) return null;

  let maxDiscount = 0;

  for (const monthlyPlan of monthlyPlans) {
    const baseSlug = monthlyPlan.slug.replace('-monthly', '');
    const yearlyPlan = yearlyPlans.find(p =>
      p.slug.replace('-yearly', '') === baseSlug
    );

    if (!yearlyPlan) continue;

    const monthlyPrice = typeof monthlyPlan.price === 'string'
      ? parseFloat(monthlyPlan.price)
      : monthlyPlan.price;
    const yearlyPrice = typeof yearlyPlan.price === 'string'
      ? parseFloat(yearlyPlan.price)
      : yearlyPlan.price;

    const yearlyMonthlyEquivalent = yearlyPrice / 12;
    const discount = ((monthlyPrice - yearlyMonthlyEquivalent) / monthlyPrice) * 100;

    if (discount > maxDiscount) {
      maxDiscount = discount;
    }
  }

  return maxDiscount > 0 ? Math.round(maxDiscount) : null;
}

export default function PricingModal({ isOpen, onClose, projectId }: PricingModalProps) {
  const { t } = useTranslation();
  const [billingPeriod, setBillingPeriod] = useState<'MONTHLY' | 'YEARLY'>('MONTHLY');
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);

  // Limpar estado de loading ao fechar o modal
  const handleClose = () => {
    setLoadingPlan(null);
    onClose();
  };

  // Fetch current subscription
  const { data: subscriptionData } = useQuery({
    queryKey: [...queryKeys.pricing, 'subscription'],
    queryFn: async () => {
      const { data } = await axiosClient.get<SubscriptionInfo>('/hub/me/subscription');
      return data;
    },
    enabled: isOpen,
  });

  // Fetch plans from Hub via Contentlife backend
  const { data: plansData, isLoading, refetch } = useQuery({
    queryKey: [...queryKeys.pricing, 'hub'],
    queryFn: async () => {
      const { data } = await axiosClient.get<PlansResponse>('/hub/billing/plans');
      return data;
    },
    enabled: isOpen,
  });

  // Calculate max yearly discount dynamically
  const maxYearlyDiscount = useMemo(() => {
    if (!plansData?.plans) return null;
    return calculateMaxYearlyDiscount(plansData.plans);
  }, [plansData?.plans]);

  // Check if user has an active paid subscription
  const currentPlanId = subscriptionData?.plan_id || null;
  const hasActivePaidPlan = subscriptionData?.is_active && currentPlanId !== null;

  // Track view_pricing when modal opens
  useEffect(() => {
    if (isOpen) {
      trackEvent('view_pricing', {
        current_plan: subscriptionData?.plan_slug || 'free',
      });
    }
  }, [isOpen, subscriptionData?.plan_slug]);

  // Checkout mutation - uses Hub billing
  const checkoutMutation = useMutation({
    mutationFn: async (planSlug: string) => {
      const { data } = await axiosClient.post<{ url: string }>(
        '/hub/billing/checkout',
        {
          plan_slug: planSlug,
          success_url: `${window.location.origin}/dashboard/project/${projectId}/settings?success=true`,
          cancel_url: `${window.location.origin}/dashboard/project/${projectId}/settings?canceled=true`,
        }
      );
      return data;
    },
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Falha ao iniciar checkout');
      setLoadingPlan(null);
    },
  });

  if (!isOpen) return null;

  // Filter plans by billing period and exclude free, sorted by price (cheapest first)
  const paidPlans = plansData?.plans
    .filter((plan) => plan.billing_period === billingPeriod && !plan.slug.includes('free'))
    .sort((a, b) => {
      const priceA = typeof a.price === 'string' ? parseFloat(a.price) : a.price;
      const priceB = typeof b.price === 'string' ? parseFloat(b.price) : b.price;
      return priceA - priceB;
    }) || [];

  // The most expensive plan is the recommended one
  const mostExpensivePlanSlug = paidPlans.length > 0
    ? paidPlans[paidPlans.length - 1].slug
    : null;

  const freePlan = plansData?.plans.find((plan) => plan.slug.includes('free'));

  const handleSubscribe = (plan: HubPlan) => {
    setLoadingPlan(plan.slug);

    // Track begin_checkout event
    const price = typeof plan.price === 'string' ? parseFloat(plan.price) : plan.price;
    trackEvent('begin_checkout', {
      plan_slug: plan.slug,
      billing_period: plan.billing_period,
      value: price,
      currency: 'BRL',
    });

    checkoutMutation.mutate(plan.slug);
  };

  const formatPrice = (price: number | string): string => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return `R$${numPrice.toFixed(2).replace('.', ',')}`;
  };

  const getMonthlyEquivalent = (plan: HubPlan): string | null => {
    if (plan.billing_period === 'YEARLY') {
      const price = typeof plan.price === 'string' ? parseFloat(plan.price) : plan.price;
      const monthly = price / 12;
      return `R$${monthly.toFixed(2).replace('.', ',')}/mês`;
    }
    return null;
  };

  const getFeatures = (plan: HubPlan): string[] => {
    const featureKeys = getPlanFeatures(plan.slug);
    const features = featureKeys.map((key) => t(key));
    if (isYearlyPlan(plan.slug)) {
      features.push(t('billing.features.twoMonthsFree'));
    }
    return features;
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-border flex justify-between items-center bg-gradient-to-r from-primary to-primary-hover">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <Crown className="w-6 h-6" />
              Escolha seu plano
            </h2>
            <p className="text-white/80">
              Desbloqueie todo o potencial do Content4Life
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center p-6 bg-secondary pb-0">
          <div className="bg-border p-1 rounded-lg flex">
            <button
              onClick={() => setBillingPeriod('MONTHLY')}
              className={`px-6 py-2.5 rounded-md text-sm font-medium transition-all ${
                billingPeriod === 'MONTHLY'
                  ? 'bg-white shadow text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Mensal
            </button>
            <button
              onClick={() => setBillingPeriod('YEARLY')}
              className={`px-6 py-2.5 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                billingPeriod === 'YEARLY'
                  ? 'bg-white shadow text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Anual
              {maxYearlyDiscount && (
                <span className="bg-accent text-white text-xs px-2 py-0.5 rounded-full font-bold">
                  -{maxYearlyDiscount}%
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Plans */}
        <div className="p-6 overflow-y-auto bg-secondary flex-1">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Carregando planos...</span>
            </div>
          ) : plansData ? (
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {/* Free Plan Card */}
              {freePlan && !hasActivePaidPlan && (
                <div className="bg-white rounded-xl shadow-sm border border-border p-6 flex flex-col">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-5 h-5 text-muted-foreground" />
                    <h3 className="text-lg font-bold text-foreground">{freePlan.name}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">{freePlan.description}</p>

                  <div className="mt-4 flex items-baseline text-foreground">
                    <span className="text-4xl font-extrabold tracking-tight">R$0</span>
                    <span className="ml-1 text-xl font-semibold text-muted-foreground">/mês</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">Grátis para sempre</p>

                  <ul className="mt-6 space-y-3 flex-1">
                    {getFeatures(freePlan).map((feature, idx) => (
                      <li key={idx} className="flex">
                        <Check className="flex-shrink-0 w-5 h-5 text-muted-foreground" />
                        <span className="ml-3 text-sm text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    disabled
                    className="mt-8 w-full py-3 px-4 rounded-lg text-sm font-medium border border-border text-muted-foreground bg-secondary cursor-not-allowed"
                  >
                    Plano Atual
                  </button>
                </div>
              )}

              {/* Paid Plan Cards */}
              {paidPlans.map((plan) => {
                const monthlyEquivalent = getMonthlyEquivalent(plan);
                const isYearly = plan.billing_period === 'YEARLY';
                // Check if this specific plan matches the user's current plan
                // Compare by plan_id for exact match
                const isCurrentPlan = hasActivePaidPlan && currentPlanId !== null && (
                  currentPlanId === String(plan.id)
                );
                // Only the most expensive plan is recommended
                const isRecommended = plan.slug === mostExpensivePlanSlug && !isCurrentPlan;

                return (
                  <div
                    key={plan.slug}
                    className={`relative bg-white rounded-xl p-6 flex flex-col ${
                      isCurrentPlan
                        ? 'shadow-lg border-2 border-accent ring-2 ring-accent/20'
                        : isRecommended
                          ? 'shadow-lg border-2 border-primary ring-2 ring-primary/20'
                          : 'shadow-sm border border-border'
                    }`}
                  >
                    {(isCurrentPlan || isRecommended) && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <span className={`text-white text-xs font-bold px-4 py-1 rounded-full ${
                          isCurrentPlan ? 'bg-accent' : 'bg-primary'
                        }`}>
                          {isCurrentPlan ? 'SEU PLANO' : 'RECOMENDADO'}
                        </span>
                      </div>
                    )}

                    <div className={`flex items-center gap-2 mb-2 ${isCurrentPlan || isRecommended ? 'mt-2' : ''}`}>
                      <Crown className={`w-5 h-5 ${
                        isCurrentPlan ? 'text-accent' : isRecommended ? 'text-primary' : 'text-muted-foreground'
                      }`} />
                      <h3 className="text-lg font-bold text-foreground">{plan.name}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">{plan.description}</p>

                    <div className="mt-4 flex items-baseline text-foreground">
                      {isYearly && monthlyEquivalent ? (
                        <>
                          <span className={`text-4xl font-extrabold tracking-tight ${
                            isCurrentPlan ? 'text-accent' : isRecommended ? 'text-primary' : 'text-foreground'
                          }`}>
                            {monthlyEquivalent.replace('/mês', '')}
                          </span>
                          <span className="ml-1 text-xl font-semibold text-muted-foreground">/mês</span>
                        </>
                      ) : (
                        <>
                          <span className={`text-4xl font-extrabold tracking-tight ${
                            isCurrentPlan ? 'text-accent' : isRecommended ? 'text-primary' : 'text-foreground'
                          }`}>
                            {formatPrice(plan.price)}
                          </span>
                          <span className="ml-1 text-xl font-semibold text-muted-foreground">/mês</span>
                        </>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {isYearly
                        ? `Cobrado ${formatPrice(plan.price)} anualmente`
                        : 'Cobrado mensalmente'}
                    </p>

                    <ul className="mt-6 space-y-3 flex-1">
                      {getFeatures(plan).map((feature, idx) => (
                        <li key={idx} className="flex">
                          <Check className="flex-shrink-0 w-5 h-5 text-accent" />
                          <span className="ml-3 text-sm text-foreground font-medium">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {isCurrentPlan ? (
                      <button
                        disabled
                        className="mt-8 w-full py-3 px-4 rounded-lg text-sm font-bold text-white bg-accent cursor-not-allowed flex items-center justify-center"
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Plano Atual
                      </button>
                    ) : (
                      <button
                        onClick={() => handleSubscribe(plan)}
                        disabled={!!loadingPlan}
                        className="mt-8 w-full py-3 px-4 rounded-lg shadow-lg text-sm font-bold text-white transition-all bg-primary hover:bg-primary-hover disabled:opacity-50 flex items-center justify-center"
                      >
                        {loadingPlan === plan.slug ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            Processando...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            Assinar Agora
                          </>
                        )}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Não foi possível carregar os planos. Tente novamente.</p>
              <button
                onClick={() => refetch()}
                className="mt-4 px-4 py-2 bg-[#1E293B] text-white rounded-lg hover:bg-[#0F172A] transition-colors"
              >
                Tentar novamente
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-secondary border-t border-border text-center text-xs text-muted-foreground">
          Pagamento seguro via Stripe. Cancele quando quiser.
        </div>
      </div>
    </div>,
    document.body
  );
}
